import { Archive, AudioLines, Download, File, FileArchive, FileSpreadsheet, FileText, Image, Link2, Music2, Pencil, Play, RotateCcw, Sparkles, Video } from "lucide-react";

import { ActionMenu } from "src/features/Stage/shared/StageKit";
import { formatBytes } from "./documentService";
import { transcriptLabel } from "./RecordingTranscript";

// AUTO-CRM-32: one document as a list row or a grid tile. Both share the same
// actions: the everyday one stays visible (Play or Download, Assign / Find lead),
// the rest live in the ⋯ menu.
export const KINDS = {
  audio: { label: "Recording", icon: Music2 },
  video: { label: "Video", icon: Video },
  document: { label: "Document", icon: FileText },
  spreadsheet: { label: "Spreadsheet", icon: FileSpreadsheet },
  image: { label: "Image", icon: Image },
  archive: { label: "Archive", icon: FileArchive },
};
// Brand palette only: blues, mint and navy.
const CATEGORY_TONES = ["#147DB5", "#2FA984", "#62B4E0", "#102F45", "#0F6392", "#8FCBEA", "#1B7A5A", "#3A9BD3"];
export const categoryTone = (categories, id) => {
  const index = (categories || []).findIndex((category) => String(category.id) === String(id));
  return index < 0 ? "#A9B6C0" : CATEGORY_TONES[index % CATEGORY_TONES.length];
};

export function whenAdded(value, now = new Date()) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const start = new Date(now); start.setHours(0, 0, 0, 0);
  const days = Math.floor((start.getTime() - new Date(date).setHours(0, 0, 0, 0)) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", ...(date.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}) });
}

// A stable, decorative waveform for recordings, from the document id.
function waveBars(seed, count = 28) {
  let hash = 0;
  for (const char of String(seed)) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return Array.from({ length: count }, (_, index) => {
    hash = (hash * 1103515245 + 12345) >>> 0;
    const envelope = Math.sin((index / (count - 1)) * Math.PI) * 0.55 + 0.35;
    return Math.round(18 + ((hash % 1000) / 1000) * 70 * envelope);
  });
}

const isRecording = (document) => ["audio", "video"].includes(document.kind);
const canPlay = (document) => document.permissions?.play && !document.archived_at && document.status === "ready" && isRecording(document);
const canFindLead = (document, leadId) => !leadId && !document.lead_id && isRecording(document) && document.permissions?.update;

// Drop empty entries, and separators at the ends or next to each other.
function tidy(items) {
  const out = [];
  items.filter(Boolean).forEach((item) => { if (item === "separator" && (!out.length || out[out.length - 1] === "separator")) return; out.push(item); });
  while (out[out.length - 1] === "separator") out.pop();
  return out;
}

function menuItems(document, { leadId, busy, act }) {
  const archived = Boolean(document.archived_at);
  const update = document.permissions?.update;
  return tidy([
    // Download is the visible action unless the file plays.
    !archived && canPlay(document) && { label: "Download", icon: Download, onClick: () => act.download(document), disabled: busy },
    update && { label: "Edit details", icon: Pencil, onClick: () => act.open("edit", { document }) },
    document.kind === "audio" && !archived && { label: "Transcript", icon: AudioLines, onClick: () => act.open("transcript", { document }) },
    document.kind === "audio" && !archived && { label: "Call summary", icon: Sparkles, onClick: () => act.open("summary", { document }) },
    "separator",
    update && { label: document.lead_id ? "Change lead" : "Assign lead", icon: Link2, onClick: () => act.open("assign", { documents: [document] }) },
    canFindLead(document, leadId) && { label: "Find lead with Appy", icon: Sparkles, onClick: () => act.open("matches", { documentIds: [document.id] }) },
    "separator",
    document.permissions?.archive && { label: "Archive", icon: Archive, danger: true, onClick: () => act.open("archive", { document }) },
  ]);
}

function LeadCell({ document, leadId, act }) {
  if (document.lead) return <span className="crm-doc-lead" title={`Linked to ${document.lead.name}`}><Link2 size={12} aria-hidden="true" />{document.lead.name}</span>;
  if (leadId) return null;
  return (
    <span className="crm-doc-link">
      {document.permissions?.update
        ? <button type="button" className="crm-doc-cta" onClick={() => act.open("assign", { documents: [document] })}><Link2 size={12} />Assign lead</button>
        : <span className="crm-doc-unlinked">Unassigned</span>}
      {canFindLead(document, leadId) && <button type="button" className="crm-doc-cta is-ai" onClick={() => act.open("matches", { documentIds: [document.id] })}><Sparkles size={12} />Find lead</button>}
    </span>
  );
}

function AiPills({ document, act, compact = false }) {
  if (document.kind !== "audio" || document.archived_at) return null;
  const transcript = document.transcription?.status;
  const summary = document.call_summary?.status;
  const tone = (status) => (["completed", "completed_with_warnings", "ready"].includes(status) ? " is-ready" : ["processing", "queued", "pending"].includes(status) ? " is-busy" : "");
  const transcriptText = transcriptLabel(transcript);
  const summaryText = summary ? transcriptLabel(summary) : "Not generated";
  if (compact) {
    return (
      <span className="crm-doc-ai-row">
        <button type="button" className={`crm-doc-ai is-compact${tone(transcript)}`} onClick={() => act.open("transcript", { document })} aria-label={`Transcript: ${transcriptText}`} title={`Transcript · ${transcriptText}`}><i aria-hidden="true" />Transcript</button>
        <button type="button" className={`crm-doc-ai is-compact${tone(summary)}`} onClick={() => act.open("summary", { document })} aria-label={`Summary: ${summaryText}`} title={`Summary · ${summaryText}`}><i aria-hidden="true" />Summary</button>
      </span>
    );
  }
  return (
    <span className="crm-doc-ai-row">
      <button type="button" className={`crm-doc-ai${tone(transcript)}`} onClick={() => act.open("transcript", { document })}><AudioLines size={12} />Transcript · {transcriptText}</button>
      <button type="button" className={`crm-doc-ai${tone(summary)}`} onClick={() => act.open("summary", { document })}><Sparkles size={12} />Summary · {summaryText}</button>
    </span>
  );
}

function PrimaryAction({ document, busy, act, compact = false }) {
  if (canPlay(document)) {
    return <button type="button" onClick={() => act.open("play", { document })} aria-label={`Play ${document.title}`} className={compact ? "crm-file-play" : "ws-btn ws-btn--primary ws-btn--sm"}><Play size={compact ? 16 : 13} />{compact ? null : "Play"}</button>;
  }
  if (document.archived_at) return null;
  return <button type="button" disabled={busy} onClick={() => act.download(document)} aria-label={`Download ${document.title}`} title="Download" className="crm-icon-btn crm-icon-btn--sm"><Download size={14} /></button>;
}

function Select({ document, selected, onSelect }) {
  if (!document.permissions?.update || !onSelect) return null;
  return <input type="checkbox" className="crm-doc-check" aria-label={`Select ${document.title}`} checked={selected} onChange={(event) => onSelect(document.id, event.target.checked)} />;
}

export function DocumentRow({ document, categories, selected, onSelect, leadId, busy, act }) {
  const kind = KINDS[document.kind] || { label: "File", icon: File };
  return (
    <li className={`crm-doc-row${selected ? " is-selected" : ""}${document.archived_at ? " is-archived" : ""}`}>
      <Select document={document} selected={selected} onSelect={onSelect} />
      <span className={`crm-doc-glyph is-${document.kind || "file"}`} aria-hidden="true"><kind.icon size={17} /></span>
      <div className="crm-doc-main">
        <p className="crm-doc-name" title={document.title}>{document.title}</p>
        <p className="crm-doc-meta"><b>{document.extension || kind.label}</b> · {formatBytes(document.size_bytes)} · {document.uploaded_by?.name || "Former member"} · {whenAdded(document.created_at)}</p>
        {document.description && <p className="crm-doc-desc">{document.description}</p>}
        <AiPills document={document} act={act} compact />
      </div>
      <div className="crm-doc-cells">
        <span className="crm-doc-cat" title="Category"><i style={{ background: categoryTone(categories, document.category?.id) }} aria-hidden="true" />{document.category?.name || "Uncategorized"}</span>
        <LeadCell document={document} leadId={leadId} act={act} />
      </div>
      <div className="crm-doc-actions">
        <PrimaryAction document={document} busy={busy} act={act} />
        {document.permissions?.restore && <button type="button" onClick={() => act.open("restore", { document })} className="ws-btn ws-btn--quiet ws-btn--sm"><RotateCcw size={13} />Restore</button>}
        <ActionMenu label={`More actions for ${document.title}`} items={menuItems(document, { leadId, busy, act })} />
      </div>
    </li>
  );
}

export function DocumentTile({ document, categories, selected, onSelect, leadId, busy, act }) {
  const kind = KINDS[document.kind] || { label: "File", icon: File };
  const recording = isRecording(document);
  return (
    <li className={`crm-file is-${document.kind || "file"}${selected ? " is-selected" : ""}${document.archived_at ? " is-archived" : ""}`}>
      <div className="crm-file-thumb">
        <span className="crm-file-ext">{document.extension || kind.label}</span>
        <Select document={document} selected={selected} onSelect={onSelect} />
        {recording ? (
          <span className="crm-file-wave" aria-hidden="true">{waveBars(document.id).map((height, index) => <i key={index} style={{ height: `${height}%` }} />)}</span>
        ) : (
          <span className="crm-file-glyph" aria-hidden="true"><kind.icon size={30} strokeWidth={1.6} /></span>
        )}
        {recording && <span className="crm-file-playwrap"><PrimaryAction document={document} busy={busy} act={act} compact /></span>}
      </div>
      <div className="crm-file-body">
        <h3 className="crm-file-name" title={document.title}>{document.title}</h3>
        <p className="crm-file-meta">{kind.label} · {formatBytes(document.size_bytes)} · {whenAdded(document.created_at)}</p>
        <div className="crm-file-tags"><LeadCell document={document} leadId={leadId} act={act} /></div>
        <AiPills document={document} act={act} compact />
      </div>
      <footer className="crm-file-foot">
        <span className="crm-doc-cat"><i style={{ background: categoryTone(categories, document.category?.id) }} aria-hidden="true" />{document.category?.name || "Uncategorized"}</span>
        <span className="crm-file-tools">
          {!recording && <PrimaryAction document={document} busy={busy} act={act} />}
          {document.permissions?.restore && <button type="button" onClick={() => act.open("restore", { document })} className="crm-icon-btn crm-icon-btn--sm" aria-label={`Restore ${document.title}`} title="Restore"><RotateCcw size={14} /></button>}
          <ActionMenu label={`More actions for ${document.title}`} items={menuItems(document, { leadId, busy, act })} />
        </span>
      </footer>
    </li>
  );
}
